import React, { useState, useEffect } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);

  useEffect(() => {
    // Check if this is a mobile device - improved detection
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) ||
                     window.innerWidth <= 768; // Also consider small screens as mobile
    
    console.log('🔍 PWA Debug: Mobile detection:', {
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints,
      innerWidth: window.innerWidth,
      isMobile
    });

    if (!isMobile) {
      console.log('🔍 PWA Debug: Not mobile, skipping PWA prompt');
      return; // Don't show PWA prompt on desktop
    }

    // Check if user has already dismissed the prompt in this session
    const dismissed = sessionStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      console.log('🔍 PWA Debug: Prompt already dismissed in this session');
      setPromptDismissed(true);
    }

    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIosStandalone = (window.navigator as any).standalone === true;
    
    if (isStandalone || isIosStandalone) {
      console.log('🔍 PWA Debug: App already installed', { isStandalone, isIosStandalone });
      setIsInstalled(true);
      return;
    }

    const handleBeforeInstallPrompt = (e: any) => {
      console.log('🔍 PWA Debug: beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a delay if not dismissed and not installed
      setTimeout(() => {
        console.log('🔍 PWA Debug: Checking if should show prompt', { dismissed, isInstalled });
        if (!dismissed && !isInstalled) {
          console.log('🔍 PWA Debug: Showing install prompt');
          setShowPrompt(true);
        }
      }, 3000); // Reduced to 3 seconds for faster testing
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      toast.success('📱 App installed successfully! You can now use it offline.');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Fallback: If no beforeinstallprompt event fires within 8 seconds, show a generic install prompt
    const fallbackTimer = setTimeout(() => {
      if (!deferredPrompt && !dismissed && !isInstalled && isMobile) {
        console.log('🔍 PWA Debug: No beforeinstallprompt event, showing fallback prompt');
        setShowPrompt(true);
      }
    }, 8000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(fallbackTimer);
    };
  }, [isInstalled, promptDismissed, deferredPrompt]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Use native install prompt if available
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          toast.success('App installation started!');
          setShowPrompt(false);
        } else {
          toast.info('App installation cancelled');
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        toast.error('Failed to install app');
      }
    } else {
      // Fallback: Show manual installation instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        toast.info('📱 To install: Tap the Share button and select "Add to Home Screen"', {
          autoClose: 8000
        });
      } else if (isAndroid) {
        toast.info('📱 To install: Tap the menu (⋮) and select "Add to Home screen" or "Install app"', {
          autoClose: 8000
        });
      } else {
        toast.info('📱 To install: Look for "Install" or "Add to Home Screen" in your browser menu', {
          autoClose: 8000
        });
      }
      
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setPromptDismissed(true);
    sessionStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (isInstalled || promptDismissed || !showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <Snackbar
      open={showPrompt}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ 
        '& .MuiSnackbarContent-root': { 
          minWidth: '300px',
          maxWidth: '400px'
        }
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <Box display="flex" gap={1}>
            <Button
              color="inherit"
              size="small"
              startIcon={<InstallIcon />}
              onClick={handleInstall}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Install
            </Button>
            <IconButton
              size="small"
              color="inherit"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <img src="/stocks.png" alt="Logo" style={{ height: 24, width: 'auto' }} />
          <strong>Install Stock Analyzer</strong>
        </Box>
        Get offline access and native app experience!
      </Alert>
    </Snackbar>
  );
};

export default PWAInstallPrompt;
